import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Select,
  Grid,
  GridItem,
  Textarea,
  Text,
  FormErrorMessage,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Checkbox,
  Flex,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";

import {
  ChevronDownIcon,
  ViewIcon,
  ViewOffIcon,
  InfoOutlineIcon,
} from "@chakra-ui/icons";
import CustomTimePicker from "views/Customers/CustomerList/CustomTimePicker";
import moment from "moment";

const DynamicFormContainer = ({
  title,
  cardData,
  formData,
  setFormData,
  width,
  errors,
  height,
  minHeight,
  maxHeight,
  editable = true,
  renderExtra,
}) => {
  const { fields } = cardData;

const handleChange = (e) => {
  const { name, value } = e.target;

  if (["phoneNumber", "phone", "second_phone"].includes(name)) {
    let input = value;

    // Always enforce +30 prefix
    if (!input.startsWith("+30")) {
      input = "+30" + input.replace(/\D/g, "");
    }

    // Remove non-digit characters except the prefix
    let digits = input.replace("+30", "").replace(/\D/g, "").slice(0, 10);

    // Determine if user is deleting
    const isDeleting = formData[name].length > value.length;

    // Format only if not deleting (to allow backspace)
    let formatted = "+30";
    if (!isDeleting) {
      if (digits.length > 0) formatted += " " + digits.slice(0, 3);
      if (digits.length > 3) formatted += " " + digits.slice(3, 7);
      if (digits.length > 6) formatted += " " + digits.slice(7, 10);
    } else {
      // Don't auto-insert spaces while deleting
      formatted += digits;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: formatted,
    }));
    return;
  }

  // Handle other inputs
  setFormData((prev) => ({
    ...prev,
    [name]: value,
  }));
};



  const handleMultiSelectChange = (name, value) => {
    setFormData((prev) => {
      const selectedValues = prev[name] || [];
      return {
        ...prev,
        [name]: selectedValues.includes(value)
          ? selectedValues.filter((item) => item !== value)
          : [...selectedValues, value],
      };
    });
  };

  const handleRemoveMultiSelect = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: prev[name].filter((item) => item !== value),
    }));
  };

  const [showPassword, setShowPassword] = useState({
    newPassword: false,
    confirmPassword: false,
  });

  const togglePasswordVisibility = (fieldName) => {
    setShowPassword((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };

  return (
    <Box
      bg="white"
      p={0}
      boxShadow="lg"
      borderRadius="lg"
      width={width}
      height={height}
      minHeight={minHeight}
      maxHeight={maxHeight}
      display="flex"
      flexDirection="column"
    >
      {/* Header */}
      <Box p={6} borderBottom="1px solid #EDF2F7">
        <Text fontWeight="700" fontSize="18px" color="rgb(31, 39, 51)">
          {title}
        </Text>
      </Box>

      {/* Scrollable Content */}
      <Box p={6} overflowY="auto" flex="1">
        <Grid templateColumns="repeat(12, 1fr)" gap={4}>
          {fields.map((field) => {
            if (field.conditional && !field.conditional(formData)) return null;

            const fieldValue = formData[field.name] ?? field.value ?? "";
            const isEditable = editable && field.editable !== false;
            const searchable = field.searchable !== false;

            return (
              <GridItem key={field.name} colSpan={field.gridSize || 12}>
                <FormControl
                  isInvalid={field.name !== "product" && !!errors[field.name]}
                >
                  {field.type !== "icon-button" && (
                    <FormLabel
                      fontWeight="700"
                      fontSize="12px"
                      color="rgb(26, 32, 44)"
                    >
                      <Flex align="center">
                        <Text as="span">{field.label}</Text>
                        {field.name === "endTime" && (
                          <Tooltip
                            label={`Η επιλεγμένη ώρα λήξης είναι πρωινή (AM) και αφορά την επόμενη ημερολογιακή ημέρα.\n\nΓια παράδειγμα, αν η ώρα έναρξης είναι 11:00 μ.μ. και η ώρα λήξης 4:00 π.μ., τότε η ώρα λήξης αναφέρεται στην επόμενη ημέρα.`}
                            fontSize="xs"
                            placement="top-start"
                            hasArrow
                          >
                            <span>
                              <InfoOutlineIcon
                                ml={1}
                                color="gray.500"
                                cursor="pointer"
                              />
                            </span>
                          </Tooltip>
                        )}
                        {field.required && (
                          <Text as="span" color="red.500">
                            *
                          </Text>
                        )}
                      </Flex>
                    </FormLabel>
                  )}

                  {/* Input Type Handling */}
                  {field.type === "password" ? (
                    <Flex align="center" position="relative" w="100%">
                      <Input
                        borderRadius="15px"
                        fontSize="xs"
                        placeholder={
                          field.placeholder === false
                            ? undefined
                            : field.placeholder || `Γράψε ${field.label}`
                        }
                        name={field.name}
                        onChange={handleChange}
                        value={fieldValue}
                        isDisabled={!isEditable}
                        type={showPassword[field.name] ? "text" : "password"}
                        pr="40px" // make room for the icon
                        autoComplete="new-password" // Disable autofill
                      />
                      <IconButton
                        icon={
                          showPassword[field.name] ? (
                            <ViewOffIcon />
                          ) : (
                            <ViewIcon />
                          )
                        }
                        onClick={() => togglePasswordVisibility(field.name)}
                        variant="ghost"
                        size="sm"
                        aria-label="Toggle Password Visibility"
                        position="absolute"
                        right="10px"
                        top="50%"
                        transform="translateY(-50%)"
                        zIndex="1" // ensures it's above the input
                        pointerEvents="auto"
                      />
                    </Flex>
                  ) : field.type === "multi-select" ? (
                    <>
                      <Wrap mb={2}>
                        {(fieldValue || []).map((value) => (
                          <Tag
                            key={value}
                            borderRadius="full"
                            variant="solid"
                            bg="teal.500"
                            color="white"
                          >
                            <TagLabel>
                              {field.options.find((opt) => opt.value === value)
                                ?.label || value}
                            </TagLabel>
                            <TagCloseButton
                              onClick={() =>
                                handleRemoveMultiSelect(field.name, value)
                              }
                            />
                          </Tag>
                        ))}
                      </Wrap>
                      <Menu closeOnSelect={false} autoSelect={false}>
                        {({ isOpen }) => {
                          const [search, setSearch] = useState("");
                          const filteredOptions = field.options.filter((opt) =>
                            opt.label
                              .toLowerCase()
                              .includes(search.toLowerCase())
                          );

                          return (
                            <>
                              <MenuButton
                                as={Button}
                                rightIcon={<ChevronDownIcon />}
                                borderRadius="md"
                                fontSize="xs"
                                fontWeight="normal"
                                textAlign="left"
                                width="100%"
                                px={4}
                                py={2}
                                bg="white"
                                border="1px solid"
                                borderColor="gray.300"
                                boxShadow="sm"
                                color="gray.600"
                                _hover={{ bg: "gray.50" }}
                              >
                                {field.placeholder === false
                                  ? " "
                                  : field.placeholder ||
                                    `Select ${field.label}`}
                              </MenuButton>
                              {isOpen && (
                                <MenuList
                                  bg="white"
                                  border="1px solid #E2E8F0"
                                  p={2}
                                  maxW="100%"
                                  zIndex={1000}
                                >
                                  {searchable && (
                                    <Box mb={3} px={2}>
                                      <Input
                                        size="sm"
                                        placeholder="Αναζήτηση…"
                                        value={search}
                                        onChange={(e) =>
                                          setSearch(e.target.value)
                                        }
                                        borderColor="gray.300"
                                        focusBorderColor="teal.400"
                                      />
                                    </Box>
                                  )}
                                  <Box maxH="170px" overflowY="auto" pr={1}>
                                    {(searchable
                                      ? filteredOptions
                                      : field.options
                                    ).map((opt) => (
                                      <MenuItem
                                        key={opt.value}
                                        onClick={() =>
                                          handleMultiSelectChange(
                                            field.name,
                                            opt.value
                                          )
                                        }
                                        _hover={{ bg: "gray.100" }}
                                        py={2}
                                      >
                                        <Flex
                                          w="100%"
                                          justify="space-between"
                                          align="center"
                                        >
                                          <Text fontSize="sm">{opt.label}</Text>
                                          <Checkbox
                                            isChecked={fieldValue.includes(
                                              opt.value
                                            )}
                                            pointerEvents="none"
                                            colorScheme="teal"
                                            size="sm"
                                          />
                                        </Flex>
                                      </MenuItem>
                                    ))}
                                  </Box>
                                </MenuList>
                              )}
                            </>
                          );
                        }}
                      </Menu>
                    </>
                  ) : field.type === "select" ? (
                    <Menu>
                      {({ isOpen }) => {
                        const [search, setSearch] = useState("");
                        const [isOpneDropdown, setIsOpneDropdown] = useState(
                          false
                        );
                        const filteredOptions = field.options.filter((opt) =>
                          opt.label.toLowerCase().includes(search.toLowerCase())
                        );
                        const selectedOption = field.options.find(
                          (opt) =>
                            opt.value === fieldValue ||
                            String(opt.value) === String(fieldValue)
                        );

                        const dropdownRef = useRef(null);
                        const inputRef = useRef(null);

                        useEffect(() => {
                          const handleClickOutside = (event) => {
                            if (
                              dropdownRef.current &&
                              !dropdownRef.current.contains(event.target)
                            ) {
                              setIsOpneDropdown(false);
                            }
                          };

                          document.addEventListener(
                            "mousedown",
                            handleClickOutside
                          );
                        }, []);

                        useEffect(() => {
                          if (isOpneDropdown && inputRef.current) {
                            inputRef.current.focus();
                          }
                        }, [isOpneDropdown]);

                        return (
                          <>
                            <div
                              ref={dropdownRef}
                              style={{ position: "relative", width: "100%" }}
                            >
                              <MenuButton
                                style={{
                                  display: "block",
                                  width: "100%",
                                  padding: "8px",
                                  borderRadius: "15px",
                                  fontSize: "12px",
                                  backgroundColor: "white",
                                  color: fieldValue ? "black" : "gray",
                                  textAlign: "left",
                                  mb: "10px",
                                  border: "1px solid #e2e8f0",
                                  height: "40px",
                                }}
                                onClick={() =>
                                  setIsOpneDropdown((prev) => !prev)
                                }
                              >
                                <Flex
                                  justify="space-between"
                                  align="center"
                                  w="100%"
                                >
                                  <Text>
                                    {selectedOption?.label ||
                                      (field.placeholder === false
                                        ? " "
                                        : field.placeholder ||
                                          `Γράψε ${field.label}`)}
                                  </Text>
                                  <ChevronDownIcon />
                                </Flex>
                              </MenuButton>

                              {isOpneDropdown && (
                                <Box
                                  sx={{
                                    zIndex: 99,
                                    padding: "4px",
                                    borderRadius: "9px",
                                    border: "1px solid #e2e8f0",
                                    position: "absolute",
                                    backgroundColor: "white",
                                    left: 0,
                                    top: 43,
                                    width: "200px",
                                    zIndex: 1000,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: "100%",
                                      backgroundColor: "white",
                                      maxHeight: "200px",
                                      overflowY: "auto",
                                      boxSizing: "border-box",
                                      scrollbarWidth: "thin",
                                      scrollbarColor:
                                        "rgb(128, 128, 128) white",
                                    }}
                                  >
                                    {searchable && (
                                      <div
                                        style={{
                                          padding: "4px",
                                          position: "sticky",
                                          top: 0,
                                          backgroundColor: "white",
                                          zIndex: 1, // Ensures input stays on top
                                        }}
                                      >
                                        <Input
                                          ref={inputRef}
                                          type="text"
                                          placeholder="Αναζήτηση…"
                                          value={search}
                                          onChange={(e) =>
                                            setSearch(e.target.value)
                                          }
                                          sx={{
                                            width: "100%",
                                            padding: "2px 4px",
                                            margin: "2px 0",
                                            minHeight: "33px",
                                            fontSize: "12px",
                                            border: "1px solid #cbd5e0",
                                            borderRadius: "5px",
                                            height: "28px",
                                          }}
                                        />
                                      </div>
                                    )}
                                    {(searchable
                                      ? filteredOptions
                                      : field.options
                                    ).map((opt) => (
                                      <Box
                                        key={opt.value}
                                        onClick={() => {
                                          setFormData((prev) => ({
                                            ...prev,
                                            [field.name]: opt.value,
                                          }));
                                          setIsOpneDropdown((prev) => !prev);
                                        }}
                                        sx={{
                                          padding: "4px",
                                          cursor: "pointer",
                                          backgroundColor: "white",
                                          "&:hover": {
                                            backgroundColor: "#a3e8e2",
                                          },
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontSize: "13px",
                                            fontWeight: "bold",
                                          }}
                                        >
                                          {" "}
                                          {opt.label}{" "}
                                        </span>
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>
                              )}
                            </div>
                          </>
                        );
                      }}
                    </Menu>
                  ) : field.type === "time" ? (
                    <CustomTimePicker
                      value={fieldValue}
                      onChange={(newValue) =>
                        setFormData((prev) => ({
                          ...prev,
                          [field.name]: newValue,
                        }))
                      }
                      placeholder={field.placeholder || `Select ${field.label}`}
                    />
                  ) : field.type === "textarea" ? (
                    <Textarea
                      name={field.name}
                      onChange={handleChange}
                      value={fieldValue}
                      minH="120px"
                      fontSize="xs"
                      borderRadius="15px"
                      placeholder={
                        field.placeholder === false
                          ? undefined
                          : field.placeholder || `Γράψε ${field.label}`
                      }
                      isDisabled={!isEditable}
                    />
                  ) : field.type === "icon-button" ? (
                    <Flex
                      align="center"
                      justify="flex-end"
                      mt={field.customStyle?.mt || "10px"} // Use custom mt if provided
                      h="40px"
                    >
                      <IconButton
                        icon={field.icon}
                        onClick={() => field.onIconClick?.(field.name)}
                        variant="ghost"
                        size="sm"
                        aria-label="icon-button"
                      />
                    </Flex>
                  ) : (
                    // <Input
                    //   borderRadius="15px"
                    //   fontSize="xs"
                    //   type={field.type}
                    //   name={field.name}
                    //   placeholder={
                    //     field.placeholder === false
                    //       ? undefined
                    //       : field.placeholder || `Γράψε ${field.label}`
                    //   }
                    //   onChange={handleChange}
                    //   value={fieldValue}
                    //   isDisabled={!isEditable}
                    // />
                    <Input
                      borderRadius="15px"
                      fontSize="xs"
                      type={field.type}
                      name={field.name}
                      placeholder={
                        field.placeholder === false
                          ? undefined
                          : field.placeholder || `Γράψε ${field.label}`
                      }
                      onChange={handleChange}
                      value={
                        !isEditable && field.displayValue !== undefined
                          ? field.displayValue
                          : fieldValue
                      }
                      isDisabled={!isEditable}
                    />
                  )}

                  <FormErrorMessage>{errors[field.name]}</FormErrorMessage>
                </FormControl>
              </GridItem>
            );
          })}
        </Grid>

        {renderExtra && <Box mt={6}>{renderExtra}</Box>}
      </Box>
    </Box>
  );
};

export default DynamicFormContainer;
